// components/CountryDetail.jsx
import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import data from '../data.json';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faMapMarkerAlt, faGlobe, faUsers, faCalendarAlt, faFileAlt } from '@fortawesome/free-solid-svg-icons';

const CountryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { europeanCountries } = data;
  
  const country = europeanCountries.find(c => c.id === parseInt(id));

  if (!country) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Country Not Found</h2>
          <p className="text-gray-600 mb-6">The country you're looking for doesn't exist.</p>
          <Link 
            to="/" 
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-300"
          >
            Back to Countries
          </Link>
        </div>
      </div>
    );
  }

 

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
      {/* Header Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-blue-600 hover:text-blue-700 font-semibold transition duration-300"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              Back to Countries
            </button>
            <div className="text-2xl">{country.flag}</div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-96 overflow-hidden">
        <img 
          src={country.image} 
          alt={country.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center mb-4">
              <span className="text-4xl mr-4">{country.flag}</span>
              <div>
                <h1 className="text-5xl font-bold mb-2">{country.name}</h1>
                <div className="flex items-center text-blue-200">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2" />
                  <span className="text-xl">Capital: {country.capital}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {country.category.map((cat, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium border border-white/30"
                >
                  {cat.toUpperCase()}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2">
            {/* Description */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <FontAwesomeIcon icon={faGlobe} className="mr-3 text-blue-500" />
                About {country.name}
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed">
                {country.description}
              </p>
            </div>

            {/* Visa Types */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <FontAwesomeIcon icon={faFileAlt} className="mr-3 text-blue-500" />
                Available Visa Types
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {country.visaTypes.map((visaType, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl p-4 hover:border-blue-300 transition duration-300">
                    <div className="flex items-center mb-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                      <h3 className="font-semibold text-gray-900 text-lg">{visaType}</h3>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Comprehensive {visaType.toLowerCase()} visa services with expert guidance.
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Requirements */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <FontAwesomeIcon icon={faUsers} className="mr-3 text-blue-500" />
                General Requirements
              </h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-4"></div>
                  <p className="text-gray-700">Valid passport with at least 6 months validity</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-4"></div>
                  <p className="text-gray-700">Proof of sufficient financial means</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-4"></div>
                  <p className="text-gray-700">Travel health insurance coverage</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-4"></div>
                  <p className="text-gray-700">Clean criminal record certificate</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Quick Facts */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Facts</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">Capital</span>
                  <span className="font-semibold text-gray-900">{country.capital}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">Category</span>
                  <span className="font-semibold text-gray-900">{country.category.join(', ')}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">Visa Types</span>
                  <span className="font-semibold text-gray-900">{country.visaTypes.length}</span>
                </div>
              </div>
            </div>

            {/* Processing Time */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-blue-500" />
                Processing Time
              </h3>
              <div className="space-y-3">
                {/* {visaRequirements.map((req, index) => (
                ))} */}
                <div  className="flex justify-between items-center py-2 border-b border-gray-100">
                  <div>
                    <div className="font-semibold text-gray-900">Average Visa Time</div>
                    {/* <div className="text-sm text-gray-500">2 - 8 weeks</div> */}
                  </div>
                  <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-medium">
                    2 - 8 weeks
                  </span>
                </div>
                <p className=''>The visa processing time depends on the type of visa you select.</p>
              </div>
            </div>

            {/* CTA Card */}
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-6 text-white text-center">
              <h3 className="text-xl font-bold mb-3">Start Your Application</h3>
              <p className="text-blue-100 mb-4 text-sm">
                Ready to begin your journey to {country.name}?
              </p>
              <Link to='/contact'>
              <button className="w-full bg-white text-blue-600 py-3 rounded-xl font-semibold hover:bg-blue-50 transition duration-300 mb-3">
                Contact Us Now!
              </button>
              </Link>
            
            </div>
          </div>
        </div>

        {/* Related Countries */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Similar Countries</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {europeanCountries
              .filter(c => c.id !== country.id && c.category.some(cat => country.category.includes(cat)))
              .slice(0, 3)
              .map(relatedCountry => (
                <Link 
                  key={relatedCountry.id} 
                  to={`/country/${relatedCountry.id}`}
                  className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition duration-300"
                >
                  <div className="h-32 overflow-hidden">
                    <img 
                      src={relatedCountry.image} 
                      alt={relatedCountry.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center mb-2">
                      <span className="text-xl mr-2">{relatedCountry.flag}</span>
                      <h3 className="font-semibold text-gray-900">{relatedCountry.name}</h3>
                    </div>
                    <p className="text-gray-600 text-sm">{relatedCountry.capital}</p>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountryDetail;